import type { ReactNode } from 'react';
import { TXProvider, useTX } from '@transifex/react';
import type PostHog from 'posthog-react-native';

import { BiometricEnrollmentProvider } from '../biometric/BiometricEnrollmentProvider';
import { setRuntimeConfig, type LoginConfig } from '../config/runtimeConfig';
import { DesignSystemProvider } from '../contexts/DesignSystem';
import { LandingFooterContext } from '../contexts/LandingFooter';
import { SheetHost } from '../components/SheetHost';
import { LoginMountProvider } from '../contexts/LoginMount';
import {
  OnAuthenticatedProvider,
  type OnAuthenticatedFn,
} from '../contexts/OnAuthenticated';
import { ThemePaletteProvider } from '../contexts/ThemePalette';
import type { Mode } from '../contexts/ThemePalette';
import { LoginStrings } from '../i18n/LoginStrings';
import { configureLoginTxToken } from '../i18n/loginTx';
import { configureLoginPostHog, setHostPostHogClient } from '../utils/analytics/posthog';
import { AnalyticsProvider } from '../utils/analytics';

type BaseLoginRootProps = {
  mountBase?: string;
  // Pins the mode. Unset, the vida-ds screens follow the color scheme and the legacy ones stay light.
  themeOverride?: Mode;
  // Absent → login falls back to `window.location.href` on native.
  onAuthenticated?: OnAuthenticatedFn;
  // vida-ds: rendered on the login screen's carousel (phone landing, and the ≥768px panel on any platform).
  landingFooter?: ReactNode;
  children: ReactNode;
};

// Discriminated: under `embedded: true` the host MUST provide `posthogClient`.
// Without it, login would fall back to constructing its own client (or throw
// on missing PostHog config), and variantFlags: undefined → posthogGetFeatureFlag
// on a null client → silent misfire.
type LoginRootProps =
  | (BaseLoginRootProps & {
      config?: Extract<LoginConfig, { embedded?: false }>;
      posthogClient?: never;
    })
  | (BaseLoginRootProps & {
      config: Extract<LoginConfig, { embedded: true }>;
      posthogClient: PostHog;
    });

export const LoginRoot = ({
  mountBase = '/',
  themeOverride,
  onAuthenticated,
  landingFooter,
  config,
  posthogClient,
  children,
}: LoginRootProps) => {
  // Publish synchronously during render — a useEffect would fire after
  // children commit and boot-time requests would hit the API before the
  // override lands.
  if (config) setRuntimeConfig(config);
  configureLoginTxToken();

  // The host's footer translates against the host's Transifex project, not login's.
  const hostTx = useTX();
  const hostFooter =
    landingFooter == null ? null : <TXProvider instance={hostTx}>{landingFooter}</TXProvider>;

  // Gate on config.embedded, not on posthogClient truthiness. A host that
  // renders <LoginRoot posthogClient={maybeNull!} /> before its client is
  // ready would otherwise fall into configureLoginPostHog() → requireBool
  // on EXPO_PUBLIC_POSTHOG_ENABLED (not inlined inside node_modules) → throw.
  if (config?.embedded) {
    if (posthogClient) setHostPostHogClient(posthogClient);
  } else {
    configureLoginPostHog();
  }

  return (
    <AnalyticsProvider>
      <DesignSystemProvider mode={themeOverride}>
        <ThemePaletteProvider override={themeOverride ?? 'light'}>
          <LoginMountProvider mountBase={mountBase}>
            <OnAuthenticatedProvider onAuthenticated={onAuthenticated}>
              <LoginStrings>
                <LandingFooterContext.Provider value={hostFooter}>
                  <BiometricEnrollmentProvider>
                    <SheetHost>{children}</SheetHost>
                  </BiometricEnrollmentProvider>
                </LandingFooterContext.Provider>
              </LoginStrings>
            </OnAuthenticatedProvider>
          </LoginMountProvider>
        </ThemePaletteProvider>
      </DesignSystemProvider>
    </AnalyticsProvider>
  );
};
